import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { requestTokenRefresh } from './authSlice';

/**
 * Wraps fetchBaseQuery with token refresh signaling on 401 responses.
 *
 * Behavior:
 * - On a 401, dispatches `requestTokenRefresh` to Redux. AuthTokenCheck
 *   subscribes to this flag via useSelector and calls getAccessTokenSilently
 *   to obtain a fresh token — or logs the user out if the session is
 *   unrecoverable. All Auth0 interaction stays inside React.
 * - The original error is always returned to the caller so the next polling
 *   interval or focus refetch will retry with the refreshed token.
 */
const StatusCodesToRefreshOn = [401, 403];

export function createBaseQueryWithReauth(
    baseQueryArgs: Parameters<typeof fetchBaseQuery>[0]
): ReturnType<typeof fetchBaseQuery> {
    const rawBaseQuery = fetchBaseQuery(baseQueryArgs);

    return async function baseQueryWithReauth(args, api, extraOptions) {
        const result = await rawBaseQuery(args, api, extraOptions);

        if (
            result.error?.status === 'PARSING_ERROR' &&
            result.meta?.request.signal.reason?.name === 'TimeoutError'
        ) {
            return {
                error: { status: 'TIMEOUT_ERROR', error: result.error.error },
                meta: result.meta,
            };
        }

        const status = result.error?.status;
        if (typeof status === 'number' && StatusCodesToRefreshOn.includes(status)) {
            api.dispatch(requestTokenRefresh());
        }

        return result;
    };
}
